import type { SnapshotPosition } from "../types/positions";

/**
 * Static metadata fields that BFF enrichment populates but the WS broadcaster path leaves null.
 * Used to preserve REST-enriched values when merging with live WS rows.
 */
const STATIC_META_FIELDS: ReadonlyArray<keyof SnapshotPosition> = [
  "instrument_family",
  "instrument_kind",
  "symbol",
  "venue_symbol",
  "primary_venue",
  "entity_name",
  "book_name",
  "denomination_ccy",
  "mark_currency",
  "functional_ccy",
  "isin",
  "underlier_symbol",
  "underlier_instrument_id",
  "underlier_mark_price",
  "contract_multiplier",
  "contract_size_snap",
  // REST-only enrichment (settle dates from booked trades, expiry horizon): a
  // compact WS tick omits them, which must not blank the cell between polls.
  "expiration_date",
  "days_to_expiry",
  "expiry_bucket",
  "quantity_pending_settlement",
  "settlement_status",
  "expected_settlement_date",
];

/** Valuation greeks / bond metrics: WS broadcaster may omit; keep REST values when live is null. */
/** pnl-engine + horizon P&L: preserve REST when WS tick omits them. */
const PNL_ATTRIBUTION_PRESERVE_FIELDS: ReadonlyArray<keyof SnapshotPosition> = [
  "unrealized_pnl_functional",
  "realized_pnl_functional_day",
  "total_pnl_functional",
  "delta_pnl_functional",
  "gamma_pnl_functional",
  "vega_pnl_functional",
  "theta_pnl_functional",
  "carry_pnl_functional",
  "dv01_pnl_functional",
  "accrued_pnl_functional",
  "vanna_pnl_functional",
  "volga_pnl_functional",
  "residual_pnl_functional",
  "attribution_coverage",
  "pnl_functional_1d",
  "pnl_functional_1w",
  "pnl_functional_1m",
  "pnl_functional_ytd",
  "pnl_functional_1y",
  "pnl_functional_itd",
  "delta_pnl_functional_1d",
  "pnl_functional_since_open",
  "invested_capital_functional",
  "holding_days",
];

const VALUATION_PRESERVE_FIELDS: ReadonlyArray<keyof SnapshotPosition> = [
  "cash_delta",
  "cash_gamma",
  "cash_vega",
  "cash_rho",
  "cash_theta",
  "cash_fx_delta",
  "rho",
  "volga",
  "vanna",
  "pv01_functional",
  "dv01_functional",
  "cs01_functional",
  "yield_to_maturity",
  "asw_spread_bps",
  "accrued_interest_functional",
  "yield_to_worst",
  "duration_modified",
  "duration_macaulay",
  "convexity",
  "z_spread_bps",
  "g_spread_bps",
  "clean_price",
  "dirty_price",
  "krd_3m_functional",
  "krd_6m_functional",
  "krd_1y_functional",
  "krd_2y_functional",
  "krd_3y_functional",
  "krd_5y_functional",
  "krd_7y_functional",
  "krd_10y_functional",
  "krd_20y_functional",
  "krd_30y_functional",
  "workout_date",
  "workout_kind",
  "clean_market_value_functional",
  "dirty_market_value_functional",
  "outstanding_notional",
  "face_amount",
];

export function financialValuesUnavailable(row: SnapshotPosition): boolean {
  const status = row.extensions?.pnl_status;
  return (typeof status === "string" && status.trim() !== "" && status.trim().toUpperCase() !== "AVAILABLE")
    || row.valuation_status === "FX_MISSING"
    || row.missing_fx_rate === true
    || row.extensions?.incomplete_fx === true;
}

function isRealizedExtension(key: string): boolean {
  return key.startsWith("realized_") || key.startsWith("cumulative_realized_");
}

function mergeFallbackFields(
  fallback: SnapshotPosition,
  primary: SnapshotPosition,
): SnapshotPosition {
  const merged: SnapshotPosition = { ...primary, extensions: { ...primary.extensions } };
  if (merged.extensions.pnl_status === undefined && !financialValuesUnavailable(primary)
    && fallback.extensions?.pnl_status !== undefined) {
    merged.extensions.pnl_status = fallback.extensions.pnl_status;
  }
  const preserveFinancial = !financialValuesUnavailable(merged);
  // REST supplies reconciliation and dated accounting evidence which a compact
  // WS tick may omit. An explicit null or newer unavailable status still wins.
  for (const [key, value] of Object.entries(fallback.extensions ?? {})) {
    if ((key === "reconciliation" || isRealizedExtension(key))
      && merged.extensions[key] === undefined
      && !(isRealizedExtension(key) && !preserveFinancial)) {
      merged.extensions[key] = value;
    }
  }
  for (const field of [
    ...STATIC_META_FIELDS,
    ...(preserveFinancial ? VALUATION_PRESERVE_FIELDS : []),
    ...(preserveFinancial ? PNL_ATTRIBUTION_PRESERVE_FIELDS : []),
  ]) {
    if (merged[field] == null && fallback[field] != null) {
      // Dynamic field copy: SnapshotPosition has ~100 optional fields; a typed
      // generic copy here would need a per-field keyof union. The cast is local
      // and bounded to this assignment.
      // biome-ignore lint/suspicious/noExplicitAny: dynamic field copy
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (merged as Record<string, any>)[field] = fallback[field];
    }
  }
  return merged;
}

function tryParseJsonRecord(s: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(s) as unknown;
    if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, unknown>;
  } catch {
    /* ignore */
  }
  return null;
}

/** JSON object record; if `data` is a string, parse once (double-encoded WS payloads). */
export function asJsonRecord(data: unknown): Record<string, unknown> | null {
  if (data == null) return null;
  if (typeof data === "string") return tryParseJsonRecord(data);
  if (typeof data === "object" && !Array.isArray(data)) return data as Record<string, unknown>;
  return null;
}

function pickArray(v: unknown): unknown[] | null {
  return Array.isArray(v) ? v : null;
}

/** Resolve positions-like arrays for REST envelopes and WS `data` objects. */
function positionsArrayFromRecord(o: Record<string, unknown>): unknown[] | null {
  const direct = pickArray(o.positions) ?? pickArray(o.rows) ?? pickArray(o.items);
  if (direct) return direct;
  const data = o.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const d = data as Record<string, unknown>;
    const nested = pickArray(d.positions) ?? pickArray(d.rows) ?? pickArray(d.items);
    if (nested) return nested;
    const d2 = d.data;
    if (d2 && typeof d2 === "object" && !Array.isArray(d2)) {
      const inner = d2 as Record<string, unknown>;
      const deep = pickArray(inner.positions) ?? pickArray(inner.rows);
      if (deep) return deep;
    }
  }
  return null;
}

export function validPositionIdentity(value: unknown): value is SnapshotPosition {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const row = value as SnapshotPosition;
  const present = (v: unknown) => typeof v === "string" && v.trim() !== "";
  const cash = row.source_system === "BOOK_CASH_LEDGER" && row.extensions?.account_scope === "BOOK_AGGREGATE"
    && row.instrument_id?.startsWith("CASH:CASH:");
  // Account costs and income (DMA, penalties, dividends) are booked per book, not per account.
  const accountCost = row.source_system === "ACCOUNT_COST_LEDGER" && row.extensions?.account_cost === true;
  return present(row.instrument_id) && present(row.book_id) && present(row.entity_id)
    && (present(row.account_id) || cash || accountCost);
}

export function positionSnapshotQualityError(raw: unknown): string | null {
  const record = asJsonRecord(raw);
  const rows = Array.isArray(raw) ? raw : record ? positionsArrayFromRecord(record) : null;
  if (!rows) return "Position snapshot has no authoritative rows; retaining the last valid view.";
  const invalid = rows.findIndex(row => !validPositionIdentity(row));
  return invalid < 0 ? null : `Position row ${invalid + 1} has incomplete identity; retaining the last valid view and requesting resynchronisation.`;
}

function coercePositionRows(rows: unknown[]): SnapshotPosition[] {
  // Full snapshots are all-or-nothing: filtering an invalid row could certify a partial image.
  return rows.every(validPositionIdentity) ? rows : [];
}

/** Accept REST / proxy envelope variants and bare arrays. */
export function normalizePositionSnapshotEnvelope(raw: unknown): SnapshotPosition[] {
  if (Array.isArray(raw)) return coercePositionRows(raw);
  const o = asJsonRecord(raw);
  if (!o) return [];
  const arr = positionsArrayFromRecord(o);
  if (!arr) return [];
  return coercePositionRows(arr);
}

/**
 * True when the response clearly describes a positions snapshot (including an explicit empty list).
 * Used to avoid `setSnapshot([])` from error-shaped or unrelated JSON that would clobber the store.
 */
export function restSnapshotDeclaresPositions(raw: unknown): boolean {
  if (Array.isArray(raw)) return true;
  const o = asJsonRecord(raw);
  if (!o) return false;
  if ("positions" in o || "rows" in o || "items" in o) return true;
  const data = o.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const d = data as Record<string, unknown>;
    if ("positions" in d || "rows" in d || "items" in d) return true;
  }
  return false;
}

export function stablePositionMapKey(row: SnapshotPosition, index: number): string {
  const k = row.position_line_key?.trim();
  if (k) return k;
  const composite = [row.instrument_id, row.book_id, row.account_id, row.snapshot_ts].join("|");
  if (composite !== "|||") return `fallback:${composite}`;
  return `fallback:idx:${index}`;
}

/**
 * Canonical key for merging REST + WS rows and for Zustand map entries (aligns with AG Grid `positionsRowId` for data rows).
 *
 * C6 fix: the previous fallback composite included `snapshot_ts`, so two WS
 * ticks for the same economic position (with no `position_line_key`) produced
 * two different keys and rendered as two grid rows — a visible "double-count".
 * `snapshot_ts` is transient (it changes every tick); it is NOT part of a
 * position's identity. The composite now uses the stable (instrument, book,
 * account, entity) tuple, matching the backend's `position_row_key`.
 */
export function positionRowDedupeKey(row: SnapshotPosition): string {
  const k = row.position_line_key?.trim();
  if (k) return k;
  const composite = [
    row.instrument_id ?? "",
    row.book_id ?? "",
    row.account_id ?? "",
    row.entity_id ?? "",
  ].join("|");
  if (composite !== "|||") return `fallback:${composite}`;
  return `row:${row.snapshot_id ?? ""}|${row.instrument_id ?? ""}|${row.book_id ?? ""}|${row.account_id ?? ""}`;
}

/**
 * Merge REST-enriched rows with live WS store rows. A demonstrably newer REST
 * snapshot wins over an older tick; otherwise WS wins. Preserve omitted metadata
 * and accounting evidence without replacing explicitly unavailable economics.
 */
export function mergePositionRowsForGrid(
  restRows: SnapshotPosition[],
  storeRows: SnapshotPosition[],
  authoritativeStore = false,
): SnapshotPosition[] {
  const storeKeys = authoritativeStore ? new Set(storeRows.map(positionRowDedupeKey)) : null;
  const restMap = new Map<string, SnapshotPosition>();
  for (const r of restRows) {
    const key = positionRowDedupeKey(r);
    if (!storeKeys || storeKeys.has(key)) restMap.set(key, r);
  }
  const m = new Map<string, SnapshotPosition>(restMap);
  for (const r of storeRows) {
    const key = positionRowDedupeKey(r);
    const restRow = restMap.get(key);
    if (!restRow) {
      m.set(key, r);
      continue;
    }
    const restTime = Date.parse(restRow.snapshot_ts);
    const liveTime = Date.parse(r.snapshot_ts);
    const restIsNewer = Number.isFinite(restTime) && Number.isFinite(liveTime) && restTime > liveTime;
    m.set(key, restIsNewer ? mergeFallbackFields(r, restRow) : mergeFallbackFields(restRow, r));
  }
  return Array.from(m.values());
}

/** WS positions `data` block: `positions` / nested envelopes / optional `removed`. */
export function parsePositionsWsData(data: unknown): { positions: SnapshotPosition[]; removed: string[] } {
  if (Array.isArray(data)) {
    return { positions: coercePositionRows(data), removed: [] };
  }
  if (typeof data === "string") {
    try {
      const v = JSON.parse(data) as unknown;
      if (Array.isArray(v)) return { positions: coercePositionRows(v), removed: [] };
    } catch {
      /* fall through to asJsonRecord */
    }
  }
  const o = asJsonRecord(data);
  if (!o) return { positions: [], removed: [] };
  const arr = positionsArrayFromRecord(o) ?? [];
  const positions = coercePositionRows(arr);
  const removed = Array.isArray(o.removed)
    ? o.removed.filter((x: unknown): x is string => typeof x === "string")
    : [];
  return { positions, removed };
}
